import React, { useRef, useState, useCallback } from 'react'
import { Title60, Body20, Eyebrow } from 'components/Typography'
import PropTypes from 'prop-types'
import { FadeInUpAnimationWrapper } from 'styles/animation'
import { bp } from 'styles/breakpoints'
import GA4 from 'utils/GA4'

import CarouselItemModal from 'components/CarouselItemModal'
import CarouselControls from 'components/Carousel/CarouselControls'
import ArticleCard from './ArticleCard'

import {
  Container,
  ProductList,
  StyledSlider,
  HeaderContainer,
  CarouselControlsWrapper,
  ArticleContainer,
  HeaderEyebrow,
  HeaderSubtitle,
  HeaderTitle,
} from './RelatedArticles.style'

const RelatedArticles = ({
  title,
  subtitle,
  eyebrow,
  articles,
  enableImageFadeIn,
  villageSlug,
}) => {
  const productModalRef = useRef(null)
  const productListRef = useRef(null)
  const [currentSlide, setCurrentSlide] = useState(0)
  const slider = useRef(null)

  const settings = {
    arrows: false,
    draggable: true,
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 3.5,
    slidesToScroll: 1,
    initialSlide: 0,
    useCSS: true,
    centerPadding: 100,
    onInit: () => {
      setCurrentSlide(0)
    },
    beforeChange: (curr, next) => {
      setCurrentSlide(Math.ceil(next))
    },
    afterChange: (curr) => {
      setCurrentSlide(Math.ceil(curr))
    },
    onEdge: () => {},
    responsive: [
      {
        breakpoint: parseInt(bp[1].replace('px', ''), 10),
        settings: {
          initialSlide: 1,
          swipeToSlide: true,
          slidesToShow: 3.5,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: parseInt(bp[0].replace('px', ''), 10),
        settings: {
          slidesToShow: 1.2,
          slidesToScroll: 1,
          initialSlide: 0,
          infinite: true,
          centerMode: true,
          centerPadding: '0px',
        },
      },
    ],
  }

  const onCarouselControlClick = useCallback(
    (i) => {
      const ceiledPosition = Math.ceil(i)

      setCurrentSlide(ceiledPosition)

      if (slider.current) {
        slider.current.slickGoTo(ceiledPosition)
      }
    },
    [slider]
  )

  return (
    <Container>
      <HeaderContainer>
        <Eyebrow as={HeaderEyebrow}>{eyebrow}</Eyebrow>
        <Title60 as={HeaderTitle}>{title}</Title60>
        <Body20 as={HeaderSubtitle}>{subtitle}</Body20>
      </HeaderContainer>
      <ProductList ref={productListRef}>
        <StyledSlider
          {...settings}
          ref={slider}
          onSwipe={() => {
            GA4('carousel_related_articles_scroll', {
              village_name: villageSlug,
              component_headline: title,
            })
          }}>
          {articles.map((article) => {
            return (
              <FadeInUpAnimationWrapper enabled={enableImageFadeIn}>
                <ArticleContainer>
                  <ArticleCard
                    {...article}
                    villageSlug={villageSlug}
                    introductionTitle={title}
                  />
                </ArticleContainer>
              </FadeInUpAnimationWrapper>
            )
          })}
        </StyledSlider>
      </ProductList>
      {articles.length >= 4 ? (
        <CarouselControlsWrapper>
          <CarouselControls
            onSlideChange={onCarouselControlClick}
            currentSlide={currentSlide}
            totalSlides={articles.length - 2}
            hasMultipleItemsPerView={false}
            isBoxed={false}
            isSmall={false}
            carouselType="RelatedArticles"
            villageName={villageSlug}
          />
        </CarouselControlsWrapper>
      ) : null}
      <CarouselItemModal ref={productModalRef} />
    </Container>
  )
}

RelatedArticles.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  eyebrow: PropTypes.string,
  articles: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])),
  enableImageFadeIn: PropTypes.bool,
  villageSlug: PropTypes.string,
}

RelatedArticles.defaultProps = {
  title: '',
  subtitle: '',
  articles: [],
  eyebrow: '',
  enableImageFadeIn: false,
  villageSlug: '',
}

export default RelatedArticles
